import React, { createContext, useContext, useEffect, useState } from 'react';
import { Product, ProductVariant } from '../api/products';

interface CartItem {
    product: Product;
    quantity: number;
    variant?: ProductVariant | null;
}

interface CartContextType {
    items: CartItem[];
    addToCart: (product: Product, quantity?: number, variant?: ProductVariant | null) => void;
    removeFromCart: (productId: number, variantId?: number | null) => void;
    updateQuantity: (productId: number, quantity: number, variantId?: number | null) => void;
    clearCart: () => void;
    total: number;
    itemCount: number;
    isCartOpen: boolean;
    setIsCartOpen: (isOpen: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'promoshop_cart';
const CART_EXPIRY_KEY = 'promoshop_cart_expiry';
const EXPIRY_DAYS = 30;

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [isCartOpen, setIsCartOpen] = useState(false);

    // Load cart from local storage
    useEffect(() => {
        const storedCart = localStorage.getItem(CART_STORAGE_KEY);
        const storedExpiry = localStorage.getItem(CART_EXPIRY_KEY);

        if (storedCart && storedExpiry) {
            const expiryDate = new Date(storedExpiry);
            if (expiryDate > new Date()) {
                setItems(JSON.parse(storedCart));
            } else {
                localStorage.removeItem(CART_STORAGE_KEY);
                localStorage.removeItem(CART_EXPIRY_KEY);
            }
        }
    }, []);

    // Save cart to local storage
    useEffect(() => {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + EXPIRY_DAYS);
        localStorage.setItem(CART_EXPIRY_KEY, expiryDate.toISOString());
    }, [items]);

    const addToCart = (product: Product, quantity = 1, variant: ProductVariant | null = null) => {
        setItems((prev) => {
            const existing = prev.find((item) =>
                item.product.id === product.id &&
                (item.variant?.id === variant?.id)
            );

            if (existing) {
                return prev.map((item) =>
                    (item.product.id === product.id && item.variant?.id === variant?.id)
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            }
            return [...prev, { product, quantity, variant }];
        });
        setIsCartOpen(true);
    };

    const removeFromCart = (productId: number, variantId?: number | null) => {
        setItems((prev) => prev.filter((item) =>
            !(item.product.id === productId && item.variant?.id === variantId)
        ));
    };

    const updateQuantity = (productId: number, quantity: number, variantId?: number | null) => {
        if (quantity < 1) {
            removeFromCart(productId, variantId);
            return;
        }
        setItems((prev) =>
            prev.map((item) =>
                (item.product.id === productId && item.variant?.id === variantId)
                    ? { ...item, quantity }
                    : item
            )
        );
    };

    const clearCart = () => {
        setItems([]);
    };

    const total = items.reduce(
        (sum, item) => {
            const price = item.variant
                ? parseFloat(item.variant.price)
                : (item.product.discount_price || item.product.price);
            return sum + price * item.quantity;
        },
        0
    );

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <CartContext.Provider
            value={{
                items,
                addToCart,
                removeFromCart,
                updateQuantity,
                clearCart,
                total,
                itemCount,
                isCartOpen,
                setIsCartOpen,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
